import axios, { AxiosError } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/** Read admin token from sessionStorage — avoids circular dependency with adminAuth store */
function getAdminToken(): string | null {
  try {
    const raw = sessionStorage.getItem('admin-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { accessToken?: string } };
    return parsed?.state?.accessToken ?? null;
  } catch {
    return null;
  }
}

export const adminApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Request interceptor: attach admin Bearer token
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: on 401 → attempt refresh, then redirect to /admin/login
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
}

/**
 * POST /auth/refresh and persist the rotated access token to the admin store so the
 * retried request is authed. The httpOnly admin_refresh_token cookie is sent
 * automatically (withCredentials). Extracted for testability.
 */
export async function handleAdminRefresh(
  client: { post: (url: string) => Promise<{ data: { accessToken: string } }> },
): Promise<string> {
  const res = await client.post('/auth/refresh');
  const { useAdminAuthStore } = await import('@/stores/adminAuth');
  useAdminAuthStore.getState().setAccessToken(res.data.accessToken);
  return res.data.accessToken;
}

adminApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosError['config'] & { _retry?: boolean };
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => adminApi(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await handleAdminRefresh(adminApi);
        processQueue(null);
        return adminApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Lazy import to avoid circular dep — only runs on 401 failure
        const { useAdminAuthStore } = await import('@/stores/adminAuth');
        useAdminAuthStore.getState().logout();
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default adminApi;
